import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, UploadCloud, Image as ImageIcon, Palette, ExternalLink, LayoutGrid, List, Code2, Droplets, Sun } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import EmbedModal from "@/components/EmbedModal";

const PRESET_COLORS = ["#007AFF", "#34C759", "#5856D6", "#FF2D55", "#FF9500", "#AF52DE", "#00C7BE", "#1D1D1F"];

export default function Branding() {
  const { user } = useAuth();
  const logoRef = useRef();
  const coverRef = useRef();
  const [branding, setBranding] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cacheBust, setCacheBust] = useState(Date.now());
  const [showEmbed, setShowEmbed] = useState(false);
  const isAdmin = user?.role === "tenant_admin";

  const load = async () => {
    try { const { data } = await api.get("/tenant/branding"); setBranding(data); }
    catch { toast.error("Failed to load branding"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/tenant/branding", { name: branding.name, accent_color: branding.accent_color, about: branding.about, layout: branding.layout, embed_theme: branding.embed_theme });
      toast.success("Branding saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/tenant/branding/${kind}`, fd);
      toast.success(`${kind === "logo" ? "Logo" : "Cover image"} updated`);
      setCacheBust(Date.now());
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!branding) return <AppLayout><div className="text-[#86868B]">Loading…</div></AppLayout>;

  const logoUrl = branding.has_logo ? `${API}/public/tenant/${branding.slug}/logo?t=${cacheBust}` : null;
  const coverUrl = branding.has_cover ? `${API}/public/tenant/${branding.slug}/cover?t=${cacheBust}` : null;

  return (
    <AppLayout>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tighter">Branding</h1>
          <p className="text-[#86868B] mt-1">Make your careers page feel like home.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowEmbed(true)} data-testid="embed-careers-btn"
            className="bg-white border border-black/10 text-[#1D1D1F] rounded-full px-4 py-2.5 font-semibold text-sm hover:bg-gray-50 active:scale-95 transition-all flex items-center gap-2">
            <Code2 size={16} /> Embed careers page
          </button>
          <a href={`/careers/${branding.slug}`} target="_blank" rel="noreferrer" data-testid="preview-careers"
            className="bg-white border border-black/10 text-[#1D1D1F] rounded-full px-4 py-2.5 font-semibold text-sm hover:bg-gray-50 active:scale-95 transition-all flex items-center gap-2">
            <ExternalLink size={16} /> Preview careers page
          </a>
        </div>
      </div>

      {!isAdmin && <p className="text-sm font-semibold text-[#B25E00] bg-[#FF9500]/10 border border-[#FF9500]/20 rounded-xl px-4 py-2.5 mb-4">Only tenant admins can edit branding.</p>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Cover */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-2 bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/5 overflow-hidden" data-testid="branding-cover-card">
          <div className="relative h-44 bg-gradient-to-br from-[#007AFF]/10 to-[#5856D6]/10 flex items-center justify-center">
            {coverUrl ? <img src={coverUrl} alt="Cover" className="w-full h-full object-cover" /> : <ImageIcon size={36} className="text-[#C7C7CC]" />}
          </div>
          <div className="p-5 flex items-center justify-between">
            <div>
              <h3 className="font-bold tracking-tight">Cover image</h3>
              <p className="text-xs text-[#86868B]">Hero background on your careers page. 1600×600 recommended.</p>
            </div>
            {isAdmin && (
              <>
                <input ref={coverRef} type="file" accept="image/*" className="hidden" data-testid="cover-input" onChange={(e) => upload("cover", e.target.files[0])} />
                <button onClick={() => coverRef.current.click()} data-testid="upload-cover-btn"
                  className="bg-[#007AFF] text-white rounded-full px-4 py-2 font-semibold text-sm hover:bg-[#0051FF] active:scale-95 transition-all flex items-center gap-2">
                  <UploadCloud size={15} /> Upload
                </button>
              </>
            )}
          </div>
        </motion.div>

        {/* Logo */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/5 p-5" data-testid="branding-logo-card">
          <h3 className="font-bold tracking-tight mb-3">Company logo</h3>
          <div className="w-20 h-20 rounded-2xl bg-[#F5F5F7] flex items-center justify-center overflow-hidden mb-4">
            {logoUrl ? <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" /> : <ImageIcon size={28} className="text-[#C7C7CC]" />}
          </div>
          {isAdmin && (
            <>
              <input ref={logoRef} type="file" accept="image/*" className="hidden" data-testid="logo-input" onChange={(e) => upload("logo", e.target.files[0])} />
              <button onClick={() => logoRef.current.click()} data-testid="upload-logo-btn"
                className="bg-white border border-black/10 rounded-full px-4 py-2 font-semibold text-sm hover:bg-gray-50 active:scale-95 transition-all flex items-center gap-2">
                <UploadCloud size={15} /> Upload logo
              </button>
            </>
          )}
        </motion.div>

        {/* Details */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="lg:col-span-3 bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/5 p-6 space-y-5">
          <div>
            <label className="text-sm font-semibold">Company name</label>
            <input value={branding.name} disabled={!isAdmin} onChange={(e) => setBranding({ ...branding, name: e.target.value })} data-testid="branding-name"
              className="mt-1 w-full bg-gray-50/70 border border-black/10 rounded-xl px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#007AFF]/30 outline-none transition-colors disabled:opacity-60" />
          </div>

          <div>
            <label className="text-sm font-semibold flex items-center gap-1.5"><Palette size={14} /> Brand accent color</label>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              {PRESET_COLORS.map((c) => (
                <button key={c} type="button" disabled={!isAdmin} onClick={() => setBranding({ ...branding, accent_color: c })} data-testid={`color-${c.replace('#','')}`}
                  className={`w-8 h-8 rounded-full transition-transform hover:scale-110 active:scale-95 ${branding.accent_color?.toLowerCase() === c.toLowerCase() ? "ring-2 ring-offset-2 ring-[#1D1D1F]" : ""}`}
                  style={{ backgroundColor: c }} aria-label={c} />
              ))}
              <div className="flex items-center gap-2 ml-1">
                <input type="color" value={branding.accent_color} disabled={!isAdmin} onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })} data-testid="color-picker"
                  className="w-8 h-8 rounded-lg border border-black/10 cursor-pointer bg-transparent" />
                <span className="text-sm font-mono text-[#86868B]">{branding.accent_color}</span>
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold">About the company</label>
            <textarea rows={4} value={branding.about} disabled={!isAdmin} onChange={(e) => setBranding({ ...branding, about: e.target.value })} data-testid="branding-about"
              placeholder="Tell candidates who you are, your mission and what makes your team a great place to work."
              className="mt-1 w-full bg-gray-50/70 border border-black/10 rounded-xl px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-[#007AFF]/30 outline-none transition-colors resize-none disabled:opacity-60" />
          </div>

          <div>
            <label className="text-sm font-semibold">Job listing view</label>
            <p className="text-xs text-[#86868B] mb-2">How open roles appear on your careers page and embeds.</p>
            <div className="inline-flex bg-gray-100 rounded-xl p-1 gap-1">
              {[
                { key: "card", label: "Cards", icon: LayoutGrid },
                { key: "list", label: "List", icon: List },
              ].map((opt) => {
                const Icon = opt.icon;
                const active = (branding.layout || "card") === opt.key;
                return (
                  <button key={opt.key} type="button" disabled={!isAdmin} onClick={() => setBranding({ ...branding, layout: opt.key })} data-testid={`layout-${opt.key}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:cursor-not-allowed ${active ? "bg-white shadow-sm text-[#1D1D1F]" : `text-[#86868B] ${isAdmin ? "hover:text-[#1D1D1F]" : "opacity-50"}`}`}>
                    <Icon size={16} /> {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold">Embed background</label>
            <p className="text-xs text-[#86868B] mb-2">Match the embed to your website — transparent blends in, light sits on a soft grey.</p>
            <div className="inline-flex bg-gray-100 rounded-xl p-1 gap-1">
              {[
                { key: "transparent", label: "Transparent", icon: Droplets },
                { key: "light", label: "Light", icon: Sun },
              ].map((opt) => {
                const Icon = opt.icon;
                const active = (branding.embed_theme || "transparent") === opt.key;
                return (
                  <button key={opt.key} type="button" disabled={!isAdmin} onClick={() => setBranding({ ...branding, embed_theme: opt.key })} data-testid={`embed-theme-${opt.key}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:cursor-not-allowed ${active ? "bg-white shadow-sm text-[#1D1D1F]" : `text-[#86868B] ${isAdmin ? "hover:text-[#1D1D1F]" : "opacity-50"}`}`}>
                    <Icon size={16} /> {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {isAdmin && (
            <button onClick={save} disabled={saving} data-testid="save-branding-btn"
              className="text-white rounded-full px-6 py-3 font-semibold text-sm active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ backgroundColor: branding.accent_color || "#007AFF" }}>
              {saving && <Loader2 size={16} className="animate-spin" />} Save changes
            </button>
          )}
        </motion.div>
      </div>

      {showEmbed && (
        <EmbedModal
          title="Embed careers page"
          description="Show only your open job listings on any website. Logo, hero and about text are hidden in the embed."
          path={`/embed/careers/${branding.slug}`}
          height={640}
          onClose={() => setShowEmbed(false)}
        />
      )}
    </AppLayout>
  );
}
